import { Outlet } from 'react-router';
import { LocationChip } from '@/components/LocationChip';
import { useGuestSession } from '@/auth/react';
import { BottomNav, SkipLink } from './nav';
import type { NavItem } from './nav';

export const CUSTOMER_NAV: NavItem[] = [
  { to: '/t/menu', label: 'Menu', icon: 'menu' },
  { to: '/t/cart', label: 'Cart', icon: 'cart' },
  { to: '/t/orders', label: 'Orders', icon: 'orders' },
  { to: '/t/bill', label: 'Bill', icon: 'bill' },
  { to: '/t/help', label: 'Help', icon: 'bell' },
];

/**
 * Mobile-first column for guests on the warm canvas: a compact venue bar that stays at the top, the screen,
 * and the tab bar. One column up to tablet width, a wider one (two menu cards across) from there; the
 * tab bar stays, because the screens are the same on every device. `showNav` is false while no table
 * session exists yet.
 */
export function CustomerShell({
  showNav = true,
  title = 'ServeFlow',
  cartCount,
  children,
}: {
  showNav?: boolean;
  /** The venue's name once it is known. */
  title?: string;
  /** Total quantity in the shared cart, shown on the Cart tab. */
  cartCount?: number;
  children?: React.ReactNode;
}) {
  const session = useGuestSession();
  const navItems = CUSTOMER_NAV.map((item) =>
    item.to === '/t/cart' ? { ...item, badge: cartCount } : item,
  );
  return (
    <div className="min-h-dvh bg-canvas">
      <div className="mx-auto flex min-h-dvh max-w-md flex-col md:max-w-2xl">
        <SkipLink />
        <header className="sticky top-0 z-20 flex min-h-14 items-center justify-between gap-3 border-b border-line bg-white px-4">
          {/* The venue name owns the row; the chip names the location only, so a long guest name never squeezes it. */}
          <span className="min-w-0 flex-1 truncate text-base font-bold text-ink">{title}</span>
          {session && <LocationChip type={session.locationType} label={session.locationLabel} />}
        </header>
        <main id="main" tabIndex={-1} className="flex flex-1 flex-col px-4 pt-4 pb-6">
          {children ?? <Outlet />}
        </main>
        {showNav && <BottomNav items={navItems} label="Customer" />}
      </div>
    </div>
  );
}
